import { useKeycloak } from '@react-keycloak/web';
import { KeycloakTokenParsed } from 'keycloak-js';
import { SigninRedirectArgs, SignoutRedirectArgs } from 'oidc-client-ts';
import { ErrorContext, useAuth } from 'react-oidc-context';

export type LoginOptions = {
    redirectUri?: string;
    locale?: string;
    idpHint?: string;
    loginHint?: string;
    prompt?: 'login' | 'none';
};

export type LogoutOptions = {
    redirectUri?: string;
};

export enum AuthErrorType {
    ALREADY_LOGIN_ERR = 'ALREADY_LOGIN_ERR',
    DIFFERENT_USER_ERR = 'DIFFERENT_USER_ERR',
    CONSENT_DENIED_ERR = 'CONSENT_DENIED_ERR',
    LOGIN_AGAIN = 'LOGIN_AGAIN',
    INVALID_SESSION_ERR = 'INVALID_SESSION_ERR',
    UNEXPECTED_ERR = 'UNEXPECTED_ERR',
    GENERAL_ERR = 'GENERAL_ERR',
    LOGIN_PAGE_EXPIRED_ERR = 'LOGIN_PAGE_EXPIRED_ERR',
}

// type Promise<void> remains only for backward compatibility
type AuthenticationMethods = {
    logout: (options: LogoutOptions) => Promise<void>;
    login: (options: LoginOptions) => Promise<void>;
};

type AuthenticationOidcData = {
    isAuthenticated: boolean;
    isLoading: boolean;
    idpSub?: string;
    token?: string;
    idToken?: string;
    urlError?: AuthErrorType | null;
    error?: ErrorContext;
};

type AuthenticationOidcMethods = {
    updateToken: (time: number) => void;
    logout: (options: SignoutRedirectArgs) => Promise<void>;
    login: (options: SigninRedirectArgs) => Promise<void>;
};

type AuthenticationData = {
    isAuthenticated: boolean;
    token?: string;
    tokenParsed?: KeycloakTokenParsed;
    idToken?: string;
    idTokenParsed?: KeycloakTokenParsed;
};

export type AuthenticationHookResult = AuthenticationData & AuthenticationMethods;
export type AuthenticationOidcHookResult = AuthenticationOidcData & AuthenticationOidcMethods;

const redirectWithHistoryEntry = (url: string) => {
    window.location.assign(url);
    return Promise.resolve();
};

const getUrlErrorType = (): AuthErrorType | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('errorType') as AuthErrorType | null;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ThrottledFunction<T extends (...args: any[]) => void> = (...args: Parameters<T>) => void;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function throttle<T extends (...args: any[]) => void>(fn: T, interval: number): ThrottledFunction<T> {
    let lastExecuted = 0;

    return function(...args: Parameters<T>) {
        const now = Date.now();

        if (now - lastExecuted >= interval) {
            lastExecuted = now;
            fn(...args);
        }
    };
}

export function useAuthentication(): AuthenticationHookResult {
    const { keycloak, initialized } = useKeycloak();

    if (initialized && keycloak?.authenticated && keycloak?.token && keycloak?.tokenParsed) {
        return {
            isAuthenticated: keycloak.authenticated,
            token: keycloak.token,
            tokenParsed: keycloak.tokenParsed,
            idToken: keycloak.idToken,
            login: (options: LoginOptions): Promise<void> => redirectWithHistoryEntry(keycloak.createLoginUrl(options)),
            logout: (options: LogoutOptions): Promise<void> =>
                redirectWithHistoryEntry(keycloak.createLogoutUrl(options)),
        };
    } else {
        return {
            isAuthenticated: false,
            login: (options: LoginOptions): Promise<void> => {
                if (!keycloak) return Promise.resolve();
                return redirectWithHistoryEntry(keycloak.createLoginUrl(options));
            },
            logout: (options: LogoutOptions): Promise<void> => {
                if (!keycloak) return Promise.resolve();
                return redirectWithHistoryEntry(keycloak.createLogoutUrl(options));
            },
        };
    }
}

export function useOidcAuthentication(): AuthenticationOidcHookResult {
    const auth = useAuth();
    const urlError = getUrlErrorType();

    return {
        isAuthenticated: auth.isAuthenticated,
        token: auth.user?.access_token,
        idToken: auth.user?.id_token,
        idpSub: auth.user?.profile?.idpSub as string | undefined,
        isLoading: auth.isLoading,
        error: auth.error,
        updateToken: (time: number) => throttle(auth.startSilentRenew, time),
        urlError,
        login: (options: SigninRedirectArgs): Promise<void> =>
            auth.isAuthenticated ? redirectWithHistoryEntry(options.redirect_uri || '') : auth.signinRedirect(options),
        logout: (options: SignoutRedirectArgs): Promise<void> => auth.signoutRedirect(options),
    };
}
